import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

export const LeadDetail: React.FC<{ leadId: string; onBack: () => void }> = ({ leadId, onBack }) => {
  const { getIdToken } = useAuth();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchLeadDetails = async () => {
    try {
      const token = await getIdToken();
      if (!token) return;
      
      const res = await fetch(`/api/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const json = await res.json();
        setData(json);
      } else {
        setData(null);
      }
    } catch (e) {
      console.error(e);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeadDetails();
  }, [leadId]);

  const updateStage = async (newStage: string) => {
    try {
      const token = await getIdToken();
      if (!token) return;
      await fetch(`/api/leads/${leadId}/stage`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ stage: newStage })
      });
      fetchLeadDetails(); // refresh
      MySwal.fire({
        toast: true,
        position: 'top-end',
        icon: 'success',
        title: 'Stage updated',
        showConfirmButton: false,
        timer: 1500
      });
    } catch (e) {
      console.error(e);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update stage.'
      });
    }
  };

  const deleteLead = async () => {
    const result = await MySwal.fire({
      title: 'Are you sure?',
      text: 'You will permanently delete this test lead!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, delete it!'
    });
    
    if (!result.isConfirmed) return;

    try {
      const token = await getIdToken();
      if (!token) return;
      const res = await fetch(`/api/leads/${leadId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        MySwal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Lead deleted',
          showConfirmButton: false,
          timer: 1500
        });
        onBack();
      } else {
        MySwal.fire({
          icon: 'error',
          title: 'Failed',
          text: 'Failed to delete lead.'
        });
      }
    } catch (e) {
      console.error(e);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error deleting lead.'
      });
    }
  };

  const sendEmail2 = async () => {
    const result = await MySwal.fire({
      title: 'Send Email 2?',
      text: 'Send the Day 2 segmentation email to this lead?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0ea5e9',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, send it!'
    });

    if (!result.isConfirmed) return;

    try {
      const token = await getIdToken();
      if (!token) return;
      const res = await fetch(`/api/leads/${leadId}/email2`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        MySwal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Email 2 sent successfully!',
          showConfirmButton: false,
          timer: 2000
        });
        fetchLeadDetails(); // refresh to show updated status
      } else {
        MySwal.fire({
          icon: 'error',
          title: 'Failed',
          text: 'Failed to send Email 2.'
        });
      }
    } catch (e) {
      console.error(e);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error sending Email 2.'
      });
    }
  };

  if (loading) return <div style={{ padding: '4rem', textAlign: 'center', color: '#64748b' }}>Loading lead details...</div>;
  if (!data) return <div style={{ padding: '4rem', textAlign: 'center', color: '#ef4444', background: '#fef2f2', borderRadius: '12px', margin: '2rem' }}>Lead not found in Dabbler.</div>;

  const isSom = data.source?.project === 'scienceofmind-ai';

  const sendSomEmail = async (type: 'spark' | 'founders' | 'defer') => {
    const actionTitles = {
      spark: 'Send The Spark (Tier 1)',
      founders: 'Invite to Founder\'s Circle',
      defer: 'Acknowledge & Defer'
    };
    
    const result = await MySwal.fire({
      title: `${actionTitles[type]}?`,
      text: `Send the ${type} email to ${data.name}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0ea5e9',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, send it!'
    });

    if (!result.isConfirmed) return;

    try {
      const token = await getIdToken();
      if (!token) return;
      const res = await fetch(`/api/leads/${leadId}/som-email`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ type })
      });
      if (res.ok) {
        MySwal.fire({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Email sent successfully!',
          showConfirmButton: false,
          timer: 2000
        });
        fetchLeadDetails(); // refresh
      } else {
        MySwal.fire({
          icon: 'error',
          title: 'Failed',
          text: 'Failed to send email.'
        });
      }
    } catch (e) {
      console.error(e);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error sending email.'
      });
    }
  };

  return (
    <div style={{ padding: '2rem', background: '#f8fafc', minHeight: 'calc(100vh - 100px)', fontFamily: 'sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <button 
          onClick={onBack}
          style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
        >
          ← Back to Leads
        </button>
        <div style={{ display: 'flex', gap: '1rem' }}>
          
          {isSom ? (
            <>
              <button 
                onClick={() => sendSomEmail('spark')}
                style={{ color: '#ffffff', background: '#0f766e', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '0.5rem 1rem', borderRadius: '6px' }}
              >
                Send The Spark (Tier 1)
              </button>
              <button 
                onClick={() => sendSomEmail('founders')}
                style={{ color: '#ffffff', background: '#0ea5e9', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '0.5rem 1rem', borderRadius: '6px' }}
              >
                Invite Founder's Circle
              </button>
              <button 
                onClick={() => sendSomEmail('defer')}
                style={{ color: '#0f172a', background: '#e2e8f0', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '0.5rem 1rem', borderRadius: '6px' }}
              >
                Acknowledge / Defer
              </button>
            </>
          ) : (
            data.source?.keyword !== 'SCORECARD' ? (
              <button 
                onClick={sendEmail2}
                disabled={data.email2Sent}
                style={{ color: data.email2Sent ? '#94a3b8' : '#ffffff', background: data.email2Sent ? '#f1f5f9' : '#0ea5e9', border: 'none', cursor: data.email2Sent ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '0.5rem 1rem', borderRadius: '6px' }}
              >
                {data.email2Sent ? 'Day 2 Sent' : 'Send Day 2 (NotebookLM)'}
              </button>
            ) : (
              <button 
                disabled
                title="This lead entered directly via the Scorecard. They should receive a different follow-up."
                style={{ color: '#94a3b8', background: '#f1f5f9', border: '1px dashed #cbd5e1', cursor: 'not-allowed', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '0.5rem 1rem', borderRadius: '6px' }}
              >
                Day 2 (N/A - Scorecard Lead)
              </button>
            )
          )}
          <button 
            onClick={deleteLead}
            style={{ color: '#ef4444', background: '#fee2e2', border: '1px solid #fecaca', cursor: 'pointer', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '0.5rem 1rem', borderRadius: '6px' }}
          >
            Delete Lead
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
        
        {/* Left Column: Profile & Actions */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: '1.875rem', fontWeight: 800, color: '#0f172a', margin: '0 0 0.5rem 0' }}>{data.name}</h2>
            <div style={{ color: '#64748b', fontWeight: 500, paddingBottom: '2rem', borderBottom: '1px solid #f1f5f9', marginBottom: '2rem' }}>
              {data.email || 'No email provided'}
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '0.75rem', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stage</span> 
                <span style={{ fontWeight: 700, color: '#1d4ed8', background: '#dbeafe', border: '1px solid #bfdbfe', padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {data.stage?.replace('_', ' ')}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '0.75rem', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Keyword</span> 
                <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.875rem' }}>{data.source?.keyword || '-'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '0.75rem', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                <span style={{ color: '#64748b', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Fit Band</span> 
                <span style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.875rem' }}>{data.fit?.scoreBand || 'Pending'}</span>
              </div>
            </div>

            <div style={{ background: '#eff6ff', padding: '1.25rem', borderRadius: '8px', border: '1px solid #dbeafe' }}>
              <h3 style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1e40af', margin: '0 0 0.75rem 0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Update Pipeline Stage</h3>
              <select 
                value={data.stage}
                onChange={(e) => updateStage(e.target.value)}
                style={{ width: '100%', padding: '0.75rem', fontSize: '0.875rem', fontWeight: 500, border: '1px solid #bfdbfe', borderRadius: '6px', background: 'white', color: '#0f172a', outline: 'none', cursor: 'pointer' }}
              >
                <option value="new">New Lead</option>
                <option value="resource_sent">Resource Sent</option>
                <option value="intake_started">Intake Started</option>
                <option value="qualified">Qualified Fit</option>
                <option value="call_booked">Call Booked</option>
                <option value="offer_sent">Offer Sent</option>
                <option value="won">Won / Enrolled</option>
                <option value="lost">Lost / Passed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Right Column: Intake & Timeline */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', gridColumn: 'span 2' }}>
          
          {/* Intake Answers */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', margin: '0 0 1.5rem 0', paddingBottom: '1rem', borderBottom: '1px solid #f1f5f9' }}>
              {isSom ? "Waitlist Intake" : "Scorecard Responses"}
            </h3>
            {isSom ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {['role', 'techProfile', 'betaExpectations', 'communitySize'].map(field => {
                  if (!data[field]) return null;
                  return (
                    <div key={field} style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem' }}>
                        {field.replace(/([A-Z])/g, ' $1').trim()}
                      </div>
                      <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#0f172a', lineHeight: 1.5 }}>
                        {String(data[field])}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : data.intake && Object.keys(data.intake).filter(k => k !== 'id').length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {Object.entries(data.intake)
                  .filter(([k]) => k !== 'id')
                  .map(([section, value]) => (
                  <div key={section} style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.75rem' }}>
                      {section.replace(/([A-Z])/g, ' $1').trim()}
                    </div>
                    {typeof value === 'object' && value !== null ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {Object.entries(value).map(([subK, subV]) => (
                          <div key={subK} style={{ fontSize: '0.875rem' }}>
                            <span style={{ fontWeight: 600, color: '#334155', marginRight: '0.5rem' }}>{subK.replace(/([A-Z])/g, ' $1').trim()}:</span>
                            <span style={{ color: '#0f172a' }}>{Array.isArray(subV) ? subV.join(', ') : String(subV)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#0f172a', lineHeight: 1.5 }}>
                        {String(value)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#64748b', fontStyle: 'italic', fontSize: '0.875rem', padding: '2rem', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #e2e8f0', textAlign: 'center' }}>
                Scorecard has not been submitted yet.
              </div>
            )}
          </div>

          {/* Activity Timeline */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '2rem', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', margin: '0 0 1.5rem 0', paddingBottom: '1rem', borderBottom: '1px solid #f1f5f9' }}>Activity Timeline</h3>
            {data.activities && data.activities.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0', borderLeft: '2px solid #dbeafe', marginLeft: '1rem', paddingLeft: '1.5rem', position: 'relative' }}>
                {data.activities.map((act: any, idx: number) => (
                  <div key={act.id} style={{ position: 'relative', marginBottom: idx === data.activities.length - 1 ? '0' : '2rem' }}>
                    {/* Timeline Dot */}
                    <div style={{ position: 'absolute', width: '0.75rem', height: '0.75rem', background: '#3b82f6', borderRadius: '50%', left: '-1.95rem', top: '0.25rem', border: '4px solid white' }}></div>
                    
                    <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.375rem' }}>
                      {act.createdAt ? new Date(act.createdAt).toLocaleString() : ''}
                    </div>
                    <div>
                      <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#0f172a', textTransform: 'capitalize' }}>
                        {act.type?.replace(/_/g, ' ')}
                      </div>
                      {act.summary && <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#475569', marginTop: '0.25rem' }}>{act.summary}</div>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: '#64748b', fontStyle: 'italic', fontSize: '0.875rem', padding: '2rem', background: '#f8fafc', borderRadius: '8px', border: '1px dashed #e2e8f0', textAlign: 'center' }}>
                No pipeline activities logged.
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};